/**
 * The FTP sidebar's row model — personal vault connections and team vault
 * connections, merged FOR DISPLAY ONLY.
 *
 * Extracted out of `FtpSidebar.js` deliberately: the decisions here (which row
 * may be edited, which may be deleted, which vault a row belongs to) are the
 * security-relevant part, and logic buried in JSX cannot be tested without
 * standing up the whole panel — which in jsdom cannot connect to anything
 * anyway.
 *
 * "Display only" is literal and load-bearing:
 *
 *   - The two lists are never combined in any STORE. `ftpStore.connections`
 *     stays exactly what it always was — `vault_list` filtered to `sftp`.
 *     Merging team items into anything derived from `vault_list` is an
 *     exploitable vulnerability rather than a tidy-up (identity substitution
 *     via the unscoped `__e2e_keypair__` lookups — see
 *     `services/teamVaultService.js`'s header and
 *     `tests/teamVaultIsolation.test.js`). Components merge these rows for
 *     rendering, through `hooks/useConnectionRows.js`, and nothing else does.
 *
 *   - Consumers that RESOLVE a credential route on `origin`, never on the id
 *     alone. The Terminal offers team rows too (`TerminalExtView`'s picker):
 *     `ssh_term_open` takes only the id, and Rust routes a `team:` key to the
 *     team vault's audited HTTP endpoint (`team_vault::ssh_credentials_for_conn`)
 *     while a personal id resolves from the local table
 *     (`vault::ssh_credentials_for_item`) exactly as before. The credential
 *     itself never reaches the webview on either path.
 *
 *   - Team rows carry `team:<teamId>:<itemId>` ids (`teamVaultService.teamItemKey`),
 *     which no personal UUID can equal. Routing decisions key off `origin`, and
 *     the id shape is the belt to that braces: a team id handed to a personal
 *     command misses and fails closed rather than resolving somebody else's
 *     credential.
 *
 *   - Every row is TAGGED with where it came from, because a user looking at a
 *     production host needs to know whether deleting it affects only them.
 */
import { teamItemKey } from '../services/teamVaultService';

export const ORIGIN_PERSONAL = 'personal';
export const ORIGIN_TEAM = 'team';
/**
 * Dialled once, never written down. Same envelope shape as a vault row so
 * every consumer treats it identically — what differs is only that removing it
 * is a local delete rather than a vault mutation, and that it is gone on
 * restart (owner, 23.09.2026).
 */
export const ORIGIN_TEMP = 'temp';

/**
 * A personal vault sftp envelope as a row.
 *
 * Always writable: the personal vault has no permission model — it is yours.
 */
export function personalConnectionRow(conn) {
  return {
    id: conn.id,
    name: conn.name,
    index: conn.index || {},
    origin: ORIGIN_PERSONAL,
    teamId: null,
    teamName: null,
    canWrite: true,
  };
}

/**
 * One team's sftp items as rows.
 *
 * `permission` comes from the SERVER, per item, already resolved for this user
 * — the client never computes it from role or from `created_by`. (Authoring an
 * item grants nothing: an author who loses their grant loses access to their
 * own item. That is BE-05, a real past vulnerability in this codebase, and
 * re-deriving permission here would be how it comes back.)
 */
export function teamConnectionRows(teamId, teamName, items) {
  return (Array.isArray(items) ? items : [])
    .filter((it) => it && it.item_type === 'sftp')
    .map((it) => ({
      id: teamItemKey(teamId, it.id),
      itemId: it.id,
      name: it.name,
      index: it.index || {},
      origin: ORIGIN_TEAM,
      teamId: String(teamId),
      teamName: teamName || '',
      version: it.version,
      folderId: it.folder_id ?? null,
      // Anything that is not exactly "write" is read-only. Fail closed: an
      // absent or unrecognised permission must never widen access.
      canWrite: it.permission === 'write',
    }));
}

/**
 * Personal rows first, then each team's, teams in the order given.
 *
 * Personal first because that is the list the user had before teams existed
 * and the solo experience must not be reshuffled by joining one.
 */
/** A session-only connection as a row. Writable: it is yours and nowhere else. */
export function tempConnectionRow(conn) {
  return {
    id: conn.id,
    name: conn.name,
    index: conn.index || {},
    origin: ORIGIN_TEMP,
    teamId: null,
    teamName: null,
    canWrite: true,
    temporary: true,
  };
}

/**
 * Temporary rows FIRST, then personal, then each team's.
 *
 * Temporary at the top because it is the one you just made and are about to
 * click, and because it disappears on restart — burying a thing with a short
 * life under a long saved list is how you lose track of it. Personal stays
 * ahead of team: that is the list the user had before teams existed, and the
 * solo experience must not be reshuffled by joining one.
 */
export function mergeConnectionRows(personalConnections, teamGroups, tempConnections) {
  const temp = (Array.isArray(tempConnections) ? tempConnections : []).map(tempConnectionRow);
  const personal = (Array.isArray(personalConnections) ? personalConnections : []).map(
    personalConnectionRow
  );
  const team = (Array.isArray(teamGroups) ? teamGroups : []).flatMap((g) =>
    teamConnectionRows(g.teamId, g.teamName, g.items)
  );
  return [...temp, ...personal, ...team];
}

/** `user@host:port` from the PLAINTEXT index — no decryption, same as before. */
export function connectionSummary(row) {
  const ix = row?.index || {};
  if (!ix.host) return '';
  return `${ix.username ? `${ix.username}@` : ''}${ix.host}${ix.port ? `:${ix.port}` : ''}`;
}

/** Name, protocol and user@host:port — everything the row actually shows. */
export function connectionMatchesQuery(row, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return `${row?.name || ''} ${String(row?.index?.protocol || '')} ${connectionSummary(row)}`
    .toLowerCase()
    .includes(q);
}

/**
 * Which "…" menu actions this row may offer.
 *
 * THE POINT OF THIS FUNCTION. A read-only team row offers no Edit and no
 * Remove — not disabled ones, not ones that open a form and fail at save.
 * `FtpSidebar` used to render its row menu unconditionally, so a user without
 * write access filled in the entire credential form before the server refused
 * it; worse, the row menu is also where Remove lives, and Remove on a shared
 * production credential is not a mistake to discover at the API boundary.
 *
 * The server is still the authority — this is the UI refusing to offer what it
 * knows will be refused, never the access check itself.
 */
export function connectionRowActions(row, connectionState) {
  const state = connectionState || 'disconnected';
  const actions = [state === 'connected' || state === 'connecting' ? 'disconnect' : 'connect'];
  if (row?.canWrite) {
    // A temporary connection has no stored item to re-open, so Edit would open
    // a form over nothing. Remove stays — it is how you get rid of one before
    // the session ends.
    if (!row.temporary) actions.push('edit');
    actions.push('remove');
  }
  return actions;
}

/** True when this row's edit/delete must go to the team API, not to `vault_*`. */
export function isTeamRow(row) {
  return row?.origin === ORIGIN_TEAM;
}
